import { PathFinder, AlgorithmOptions } from '../core/PathFinder';
import { PathResult, AlgorithmStep } from '../core/PathResult';
import { BaseGrid } from '@/grid/BaseGrid';
import { Cell, CellState } from '@/grid/Cell';
import { Heuristic } from '../core/Heuristic';
import { PriorityQueue } from '@/utils/PriorityQueue';
import { SquareGrid } from '@/grid/SquareGrid';

export class JumpPointSearch extends PathFinder {
  readonly name = 'Jump Point Search';
  readonly description = 'Optimized A* for uniform-cost grids with diagonal movement';

  findPath(grid: BaseGrid, start: Cell, goal: Cell, options?: AlgorithmOptions): PathResult {
    const startTime = performance.now();
    const steps: AlgorithmStep[] = [];

    const generator = this.findPathStepByStep(grid, start, goal, options);

    let result = generator.next();
    while (!result.done) {
      steps.push(result.value);
      result = generator.next();
    }

    const executionTime = performance.now() - startTime;
    const pathResult = result.value;

    return new PathResult(
      pathResult.path,
      pathResult.nodesExplored,
      executionTime,
      pathResult.success,
      steps
    );
  }

  *findPathStepByStep(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    options?: AlgorithmOptions
  ): Generator<AlgorithmStep, PathResult, undefined> {
    const startTime = performance.now();
    const heuristic = options?.heuristic || Heuristic.octile;

    if (!(grid instanceof SquareGrid)) {
      return yield* this.fallbackAStar(grid, start, goal, heuristic, startTime);
    }

    const squareGrid = grid as SquareGrid;
    if (!squareGrid.allowDiagonal) {
      return yield* this.fallbackAStar(grid, start, goal, heuristic, startTime);
    }

    this.lastGrid = squareGrid;
    grid.reset();

    const openSet = new PriorityQueue<Cell>();
    const closedSet = new Set<Cell>();

    start.gCost = 0;
    start.hCost = heuristic(start, goal);
    start.fCost = start.gCost + start.hCost;
    start.parent = null;

    openSet.enqueue(start, start.fCost);
    start.state = CellState.OPEN;

    yield {
      type: 'open',
      cell: start,
      cost: start.fCost,
      openListSize: openSet.size,
      closedListSize: closedSet.size,
      metadata: { gCost: start.gCost, hCost: start.hCost }
    };

    let nodesExplored = 0;

    while (!openSet.isEmpty()) {
      const current = openSet.dequeue();
      if (!current) break;

      if (current.equals(goal)) {
        const path = this.reconstructPath(goal);
        const executionTime = performance.now() - startTime;

        yield {
          type: 'path_found',
          cell: current,
          cost: current.gCost,
          openListSize: openSet.size,
          closedListSize: closedSet.size
        };

        return new PathResult(path, nodesExplored, executionTime, true);
      }

      closedSet.add(current);
      current.state = CellState.CLOSED;
      nodesExplored++;

      yield {
        type: 'close',
        cell: current,
        cost: current.fCost,
        openListSize: openSet.size,
        closedListSize: closedSet.size,
        metadata: { gCost: current.gCost, hCost: current.hCost }
      };

      const successors = this.identifySuccessors(squareGrid, current, goal, closedSet);

      for (const successor of successors) {
        if (closedSet.has(successor.cell)) {
          continue;
        }

        const tentativeGCost = current.gCost + successor.distance;

        if (tentativeGCost < successor.cell.gCost) {
          successor.cell.parent = current;
          successor.cell.gCost = tentativeGCost;
          successor.cell.hCost = heuristic(successor.cell, goal);
          successor.cell.fCost = successor.cell.gCost + successor.cell.hCost;

          const isInOpenSet = openSet.contains(successor.cell, (a, b) => a.equals(b));

          if (isInOpenSet) {
            openSet.updatePriority(successor.cell, successor.cell.fCost, (a, b) => a.equals(b));
          } else {
            openSet.enqueue(successor.cell, successor.cell.fCost);
            successor.cell.state = CellState.OPEN;

            yield {
              type: 'open',
              cell: successor.cell,
              cost: successor.cell.fCost,
              parent: current,
              openListSize: openSet.size,
              closedListSize: closedSet.size,
              metadata: { gCost: successor.cell.gCost, hCost: successor.cell.hCost }
            };
          }
        }
      }
    }

    const executionTime = performance.now() - startTime;
    return PathResult.failure(nodesExplored, executionTime);
  }

  private *fallbackAStar(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    heuristic: (a: Cell, b: Cell) => number,
    startTime: number
  ): Generator<AlgorithmStep, PathResult, undefined> {
    grid.reset();

    const openSet = new PriorityQueue<Cell>();
    const closedSet = new Set<Cell>();

    start.gCost = 0;
    start.hCost = heuristic(start, goal);
    start.fCost = start.gCost + start.hCost;
    start.parent = null;

    openSet.enqueue(start, start.fCost);
    start.state = CellState.OPEN;

    yield {
      type: 'open',
      cell: start,
      cost: start.fCost,
      openListSize: openSet.size,
      closedListSize: closedSet.size,
      metadata: { gCost: start.gCost, hCost: start.hCost }
    };

    let nodesExplored = 0;

    while (!openSet.isEmpty()) {
      const current = openSet.dequeue();
      if (!current) break;

      if (current.equals(goal)) {
        const path = this.reconstructPath(goal);
        const executionTime = performance.now() - startTime;

        yield {
          type: 'path_found',
          cell: current,
          cost: current.gCost,
          openListSize: openSet.size,
          closedListSize: closedSet.size
        };

        return new PathResult(path, nodesExplored, executionTime, true);
      }

      closedSet.add(current);
      current.state = CellState.CLOSED;
      nodesExplored++;

      yield {
        type: 'close',
        cell: current,
        cost: current.fCost,
        openListSize: openSet.size,
        closedListSize: closedSet.size,
        metadata: { gCost: current.gCost, hCost: current.hCost }
      };

      const neighbors = grid.getNeighbors(current);

      for (const neighbor of neighbors) {
        if (closedSet.has(neighbor)) {
          continue;
        }

        const tentativeGCost = current.gCost + grid.calculateDistance(current, neighbor) * neighbor.weight;

        if (tentativeGCost < neighbor.gCost) {
          neighbor.parent = current;
          neighbor.gCost = tentativeGCost;
          neighbor.hCost = heuristic(neighbor, goal);
          neighbor.fCost = neighbor.gCost + neighbor.hCost;

          const isInOpenSet = openSet.contains(neighbor, (a, b) => a.equals(b));

          if (isInOpenSet) {
            openSet.updatePriority(neighbor, neighbor.fCost, (a, b) => a.equals(b));
          } else {
            openSet.enqueue(neighbor, neighbor.fCost);
            neighbor.state = CellState.OPEN;

            yield {
              type: 'open',
              cell: neighbor,
              cost: neighbor.fCost,
              parent: current,
              openListSize: openSet.size,
              closedListSize: closedSet.size,
              metadata: { gCost: neighbor.gCost, hCost: neighbor.hCost }
            };
          }
        }
      }
    }

    const executionTime = performance.now() - startTime;
    return PathResult.failure(nodesExplored, executionTime);
  }

  private identifySuccessors(
    grid: SquareGrid,
    current: Cell,
    goal: Cell,
    closedSet: Set<Cell>
  ): { cell: Cell; distance: number }[] {
    const successors: { cell: Cell; distance: number }[] = [];
    const neighbors = this.findNeighbors(grid, current);

    for (const neighbor of neighbors) {
      const dx = neighbor.col - current.col;
      const dy = neighbor.row - current.row;

      const jumpPoint = this.jump(grid, neighbor.col, neighbor.row, dx, dy, goal);

      if (jumpPoint) {
        const jumpCell = grid.getCell(jumpPoint.row, jumpPoint.col);
        if (jumpCell && !closedSet.has(jumpCell)) {
          const distance = this.calculateJumpDistance(current, jumpCell);
          successors.push({ cell: jumpCell, distance });
        }
      }
    }

    return successors;
  }

  private findNeighbors(grid: SquareGrid, cell: Cell): Cell[] {
    const neighbors: Cell[] = [];
    const parent = cell.parent;

    if (!parent) {
      return grid.getNeighbors(cell);
    }

    const dx = Math.sign(cell.col - parent.col);
    const dy = Math.sign(cell.row - parent.row);

    if (dx !== 0 && dy !== 0) {
      const up = grid.getCell(cell.row + dy, cell.col);
      const right = grid.getCell(cell.row, cell.col + dx);
      const diag = grid.getCell(cell.row + dy, cell.col + dx);

      if (up?.isWalkable) neighbors.push(up);
      if (right?.isWalkable) neighbors.push(right);
      if (diag?.isWalkable && (up?.isWalkable || right?.isWalkable)) {
        neighbors.push(diag);
      }

      const leftBlocked = !grid.getCell(cell.row, cell.col - dx)?.isWalkable;
      const downBlocked = !grid.getCell(cell.row - dy, cell.col)?.isWalkable;

      if (leftBlocked && up?.isWalkable) {
        const forcedUp = grid.getCell(cell.row + dy, cell.col - dx);
        if (forcedUp?.isWalkable) neighbors.push(forcedUp);
      }
      if (downBlocked && right?.isWalkable) {
        const forcedRight = grid.getCell(cell.row - dy, cell.col + dx);
        if (forcedRight?.isWalkable) neighbors.push(forcedRight);
      }
    } else if (dx !== 0) {
      const right = grid.getCell(cell.row, cell.col + dx);
      if (right?.isWalkable) {
        neighbors.push(right);

        const upBlocked = !grid.getCell(cell.row - 1, cell.col)?.isWalkable;
        const downBlocked = !grid.getCell(cell.row + 1, cell.col)?.isWalkable;

        if (upBlocked) {
          const forcedUp = grid.getCell(cell.row - 1, cell.col + dx);
          if (forcedUp?.isWalkable) neighbors.push(forcedUp);
        }
        if (downBlocked) {
          const forcedDown = grid.getCell(cell.row + 1, cell.col + dx);
          if (forcedDown?.isWalkable) neighbors.push(forcedDown);
        }
      }
    } else if (dy !== 0) {
      const up = grid.getCell(cell.row + dy, cell.col);
      if (up?.isWalkable) {
        neighbors.push(up);

        const leftBlocked = !grid.getCell(cell.row, cell.col - 1)?.isWalkable;
        const rightBlocked = !grid.getCell(cell.row, cell.col + 1)?.isWalkable;

        if (leftBlocked) {
          const forcedLeft = grid.getCell(cell.row + dy, cell.col - 1);
          if (forcedLeft?.isWalkable) neighbors.push(forcedLeft);
        }
        if (rightBlocked) {
          const forcedRight = grid.getCell(cell.row + dy, cell.col + 1);
          if (forcedRight?.isWalkable) neighbors.push(forcedRight);
        }
      }
    }

    return neighbors;
  }

  private jump(
    grid: SquareGrid,
    col: number,
    row: number,
    dx: number,
    dy: number,
    goal: Cell
  ): { row: number; col: number } | null {
    const cell = grid.getCell(row, col);
    if (!cell || !cell.isWalkable) {
      return null;
    }

    if (cell.row === goal.row && cell.col === goal.col) {
      return { row, col };
    }

    if (dx !== 0 && dy !== 0) {
      const leftBlocked = !grid.getCell(row, col - dx)?.isWalkable;
      const downBlocked = !grid.getCell(row - dy, col)?.isWalkable;

      if ((leftBlocked && grid.getCell(row + dy, col - dx)?.isWalkable) ||
          (downBlocked && grid.getCell(row - dy, col + dx)?.isWalkable)) {
        return { row, col };
      }

      if (this.jump(grid, col + dx, row, dx, 0, goal) !== null ||
          this.jump(grid, col, row + dy, 0, dy, goal) !== null) {
        return { row, col };
      }
    } else {
      if (dx !== 0) {
        const upBlocked = !grid.getCell(row - 1, col)?.isWalkable;
        const downBlocked = !grid.getCell(row + 1, col)?.isWalkable;

        if ((upBlocked && grid.getCell(row - 1, col + dx)?.isWalkable) ||
            (downBlocked && grid.getCell(row + 1, col + dx)?.isWalkable)) {
          return { row, col };
        }
      } else {
        const leftBlocked = !grid.getCell(row, col - 1)?.isWalkable;
        const rightBlocked = !grid.getCell(row, col + 1)?.isWalkable;

        if ((leftBlocked && grid.getCell(row + dy, col - 1)?.isWalkable) ||
            (rightBlocked && grid.getCell(row + dy, col + 1)?.isWalkable)) {
          return { row, col };
        }
      }
    }

    const nextCell = grid.getCell(row + dy, col + dx);
    if (!nextCell?.isWalkable) {
      return null;
    }

    if (dx !== 0 && dy !== 0) {
      const horizontalCell = grid.getCell(row, col + dx);
      const verticalCell = grid.getCell(row + dy, col);
      if (!horizontalCell?.isWalkable && !verticalCell?.isWalkable) {
        return null;
      }
    }

    return this.jump(grid, col + dx, row + dy, dx, dy, goal);
  }

  private calculateJumpDistance(from: Cell, to: Cell): number {
    const dx = Math.abs(to.col - from.col);
    const dy = Math.abs(to.row - from.row);
    const diag = Math.min(dx, dy);
    const straight = Math.abs(dx - dy);
    return diag * Math.SQRT2 + straight;
  }

  protected reconstructPath(goal: Cell): Cell[] {
    const jumpPoints: Cell[] = [];
    let current: Cell | null = goal;

    while (current !== null) {
      jumpPoints.unshift(current);
      current = current.parent;
    }

    if (jumpPoints.length <= 1) {
      return jumpPoints;
    }

    const fullPath: Cell[] = [jumpPoints[0]];

    for (let i = 0; i < jumpPoints.length - 1; i++) {
      const from = jumpPoints[i];
      const to = jumpPoints[i + 1];
      const intermediateCells = this.getIntermediateCells(from, to);
      fullPath.push(...intermediateCells);
      fullPath.push(to);
    }

    return fullPath;
  }

  private getIntermediateCells(from: Cell, to: Cell): Cell[] {
    const cells: Cell[] = [];
    const dx = Math.sign(to.col - from.col);
    const dy = Math.sign(to.row - from.row);

    let currentRow = from.row;
    let currentCol = from.col;

    while (currentRow !== to.row || currentCol !== to.col) {
      if (currentCol !== to.col) {
        currentCol += dx;
      }
      if (currentRow !== to.row) {
        currentRow += dy;
      }

      if (currentRow === to.row && currentCol === to.col) {
        break;
      }

      const cell = this.lastGrid?.getCell(currentRow, currentCol);
      if (cell) {
        cells.push(cell);
      }
    }

    return cells;
  }

  private lastGrid: SquareGrid | null = null;
}
